import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  activityItemContent, blockNavigation, isNavigationBlocked, promptNavigation, toggleListSelection
} from 'library/helpers';
import _ from 'lodash';
import {
  addTimelineActivity,
  deleteTimelineActivity,
  deleteTimelineGroup,
  loadTemplateTimeline,
  moveTimelineActivities,
  newTemplateTimeline,
  reorderTimelineGroup,
  resizeTimelineActivities,
  saveTemplateTimeline,
  selectTimelineActivities,
  updateTimelineActivities,
  updatePinnedActivities,
} from 'actions/template.actions';
import { list } from 'actions';
import { connect } from 'react-redux';
import { history } from 'store';
import { TIMELINE_DAYS_TO_SHOW, TIMELINE_START_DATE } from 'selectors/timeline';
import REDUCER_KEY from 'reducers/reducer.keys';

import KeyHandler from 'react-key-handler';
import HeaderBar from 'components/headerBar/header-bar';
import ToolBar from 'components/toolBar/tool-bar';
import SlidePanel from 'components/slidePanel/slide-panel';

import {
  Button, Drawer, Icon, message, Modal, Spin, Tabs, Tooltip
} from 'antd';
import {
  GroupRenderer, NotificationsPanel, Sidebar, TemplateItemRenderer
} from 'components/Timeline';
import { Notifications } from 'views/shared';
import { NavigationPrompt } from 'components/Menu';
import Timeline from '../../../../libs/timeline/timeline';
import ActivityInput from './activityForm/ActivityInput';
import { defaultSettings, filters, pickers } from './config';

const { TabPane } = Tabs;

const activitySelector = activity => ({
  title: activity.title,
  key: activity.id,
  row: activity.group,
  start: moment.isMoment(activity.start_time) ? activity.start_time : moment(activity.start_time),
  end: moment.isMoment(activity.end_time) ? activity.end_time : moment(activity.end_time),
  color: activity.color,
  activity: activity.activity,
  default_notifications: activity.default_notifications
});

const { TIMELINE_MODES } = Timeline;
const DEFAULT_TIMELINE_ACTIVITY_CREATE_COUNT = 1;
const DEFAULT_TIMELINE_ACTIVITY_HOURS_BETWEEN = 24;
const DEFAULT_TIMELINE_MAX_DAYS = 7;
const DEFAULT_TIMELINE_SNAP_MINUTES = 15;
const MIN_ZOOM_DAYS = 1;
const MAX_ZOOM_DAYS = TIMELINE_DAYS_TO_SHOW;
const MAX_DAYS = TIMELINE_DAYS_TO_SHOW;

const MOUSE_RIGHT_CLICK = 2;
const DELETE_KEY_CODE = 46;
const BACKSPACE_KEY_CODE = 8;
const CTRL_KEY_CODE = 17;

// Also used in template mapper
export const TIMEBAR_FORMAT = {
  majorLabels: {
    minute: {
      short: 'mm', // 01
      long: 'HH:mm' // 12:01
    },
    hour: {
      short: 'HH', // 13
      long: 'HH:mm' // 13:00
    },
    day: {
      short: 'DDD', // 1st
      long: ' [Day] DDD' // Sun, July 3, 2018
    },
    month: {
      short: 'w[+]', // Jan
      long: '[Weeks] w[+]' // January 2018
    },
    year: {
      short: `[${MAX_DAYS}]`,
      long: `[${MAX_DAYS} days]`
    }
  },
  minorLabels: {
    minute: {
      short: 'mm', // 01
      long: 'HH:mm' // 12:01
    },
    hour: {
      short: 'HH', // 13
      long: 'HH:mm' // 13:00
    },
    day: {
      short: 'DDD', // 1
      long: '[Day] DDD' // Sun 1st
    },
    month: {
      short: 'w[+]', // 02
      long: '[Weeks] w[+]' // January
    },
    year: {
      short: '[]', // 2018
      long: '[]' // 2018
    }
  }
};

const mapStateToProps = state => ({
  template: state.templateTimeline.template,
  activities: state.api.activities.data.results,
  groups: state.templateTimeline.groups,
  items: state.templateTimeline.items,
  selected: state.templateTimeline.selected,
  inFlight: state.templateTimeline.inFlight,
  pinnedActivities: state.templateTimeline.templatePinnedActivities,
});

const isBitSet = (bit, mask) => (bit & mask) === bit;

const mapDispatchToProps = dispatch => ({
  loadActivityList() {
    dispatch(list(
      REDUCER_KEY.activities.key, {
        page: 0,
        pageSize: 1000,
        filters: [],
        sorted: [{ id: 'name' }]
      }
    ));
  },
  dispatchNewTemplateTimeline() {
    dispatch(newTemplateTimeline());
  },
  dispatchLoadTemplateTimeline(templateId) {
    return dispatch(loadTemplateTimeline(templateId));
  },
  dispatchSaveTemplateTimeline(templateId, activities = []) {
    return dispatch(saveTemplateTimeline(templateId, activities));
  },

  dispatchAddTimelineActivity(activity, group, time, createActivityCount, hoursBetween) {
    dispatch(addTimelineActivity(activity, group, time, createActivityCount, hoursBetween));
  },
  dispatchUpdateTimelineActivities(items) {
    dispatch(updateTimelineActivities(items));
  },
  dispatchDeleteTimelineActivity() {
    dispatch(deleteTimelineActivity());
  },

  dispatchSelectTimelineActivities(activityIds = []) {
    dispatch(selectTimelineActivities(activityIds));
  },
  dispatchMoveTimelineActivities(changes, items) {
    dispatch(moveTimelineActivities(changes, items));
  },
  dispatchResizeTimelineActivities(changes, items) {
    dispatch(resizeTimelineActivities(changes, items));
  },

  dispatchReorderTimelineGroup(fromGroupId, toGroupId) {
    dispatch(reorderTimelineGroup(fromGroupId, toGroupId));
  },
  dispatchDeleteTimelineGroup(groupId) {
    dispatch(deleteTimelineGroup(groupId));
  },
  dispatchUpdatePinnedActivities(activities) {
    dispatch(updatePinnedActivities(activities));
  },
});

/**
 * Template timeline and toolbar
 *
 * @version 1.0.1
 */
class TemplateTimeline extends Component {
  static handleGroupDragStart(e, group) {
    e.dataTransfer.setData('fromGroup', JSON.stringify(group));
  }

  constructor(props) {
    super(props);

    this.MIN_VISIBLE_DATE = moment(TIMELINE_START_DATE);
    this.MAX_VISIBLE_DATE = moment(this.MIN_VISIBLE_DATE)
      .add(TIMELINE_DAYS_TO_SHOW, 'day');
    this.mouseClicks = 0;

    this.state = {
      selectedToolbarItem: null,
      selectedActivity: null,
      selectedActivities: [],

      showToolbar: true,
      showActivityForm: false,
      showBulkActivityForm: false,

      timelineMode: TIMELINE_MODES.SELECT | TIMELINE_MODES.DRAG,

      // This object retains the current timeline option
      // settings selected by the user.
      timelineSettings: {
        ...defaultSettings,
        options: {
          activityCreateCount: DEFAULT_TIMELINE_ACTIVITY_CREATE_COUNT,
          activityHoursBetween: DEFAULT_TIMELINE_ACTIVITY_HOURS_BETWEEN,
          timelineMaxDays: DEFAULT_TIMELINE_MAX_DAYS,
          timelineSnapMinutes: DEFAULT_TIMELINE_SNAP_MINUTES,
          displayTravelLayer: false,
          displayLeaveLayer: false,
          displayAvailabilityLayer: false,
          rosterPivoted: false,
          hideEmptyRows: false
        }
      },

      // This object stores the calculated activity filters
      // to apply to the template timeline.
      customFilters: {
        activity: {
          active: {},
          ids: {},
          item: {}
        }
      },

      visibleTimeStart: this.MIN_VISIBLE_DATE,
      visibleTimeEnd: moment(this.MIN_VISIBLE_DATE)
        .add(DEFAULT_TIMELINE_MAX_DAYS, 'day')
    };

    this.handleMouseWheel = this.handleMouseWheel.bind(this);
    this.handleSaveTemplate = this.handleSaveTemplate.bind(this);

    this.handleDeleteKeydown = this.handleDeleteKeydown.bind(this);
    this.handleCtrlKeyup = this.handleCtrlKeyup.bind(this);
    this.handleCtrlKeydown = this.handleCtrlKeydown.bind(this);

    this.handleZoomIn = this.handleZoomIn.bind(this);
    this.handleZoomOut = this.handleZoomOut.bind(this);
    this.handlePanBack = this.handlePanBack.bind(this);
    this.handlePanForward = this.handlePanForward.bind(this);

    this.handleSingleMouseClick = this.handleSingleMouseClick.bind(this);

    this.handleItemClicks = this.handleItemClicks.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.handleRowDoubleClick = this.handleRowDoubleClick.bind(this);
    this.handleTimelineInteraction = this.handleTimelineInteraction.bind(this);

    this.handleShowToolbar = this.handleShowToolbar.bind(this);
    this.handleTimelineToolbarItemChange = this.handleTimelineToolbarItemChange.bind(this);
    this.handleCustomFilters = this.handleCustomFilters.bind(this);
    this.handleTimelineToolbarOptionsChange = this.handleTimelineToolbarOptionsChange.bind(this);
    this.handlePinItem = this.handlePinItem.bind(this);

    this.handleGroupDragDrop = this.handleGroupDragDrop.bind(this);
    this.handleGroupDelete = this.handleGroupDelete.bind(this);

    this.handleActivityFormModalOk = this.handleActivityFormModalOk.bind(this);
    this.handleActivityFormModalCancel = this.handleActivityFormModalCancel.bind(this);
    this.filterActivities = this.filterActivities.bind(this);

    this.handleItemContextClick = this.handleItemContextClick.bind(this);
    this.renderGroupTitle = this.renderGroupTitle.bind(this);

    this.handleBulkActivityModalCancel = this.handleBulkActivityModalCancel.bind(this);

    this.unselectActivities = this.unselectActivities.bind(this);
  }

  componentDidMount() {
    const {
      loadActivityList, dispatchSelectTimelineActivities, dispatchNewTemplateTimeline, dispatchLoadTemplateTimeline, match
    } = this.props;

    const { params, router } = match;
    const templateId = params.id;

    loadActivityList();

    if (templateId) {
      dispatchLoadTemplateTimeline(templateId)
        .catch((e) => {
          if (e.respons && e.response.status === 404) history.push('/roster/templates/');
        });
    }
    else {
      dispatchNewTemplateTimeline();
    }

    if (isNavigationBlocked()) {
      history.block((nextLocation) => {
        if (isNavigationBlocked()) {
          promptNavigation(() => {
          },
          () => {
            // Discard changes
            blockNavigation(false);
            router.push(nextLocation);
          });
          return false;
        }
        return true;
      });
    }
    dispatchSelectTimelineActivities();
  }

  componentWillUnmount() {
    const { dispatchNewTemplateTimeline, dispatchSelectTimelineActivities } = this.props;
    blockNavigation(false);

    // Clear redux store for the templates and selected activities
    dispatchNewTemplateTimeline();
    dispatchSelectTimelineActivities();
  }

  getTimelineContainerRef = (input) => {
    this.timelineContainer = input;
  };

  getActivityFormRef = (form) => {
    this.activityForm = form;
  };

  filterActivities(data) {
    const { customFilters } = this.state;

    const { active = {}, item, ids } = customFilters.activity;

    const name = item.searchValue || '';
    const activities = ids.searchValue || [];

    let matched = true;
    if (name.length > 0) {
      matched = matched && data.title.toLowerCase()
        .includes(name.toLowerCase());
    }
    if (activities.length) {
      matched = matched && activities.includes(data.activity.id);
    }
    if (active.searchValue) {
      matched = matched && data.activity.active;
    }
    return matched;
  }

  saveTemplate() {
    const { match, items, dispatchSaveTemplateTimeline } = this.props;
    const { params } = match;
    const templateId = params.id;

    dispatchSaveTemplateTimeline(templateId, items)
      .then(
        () => {
          blockNavigation(false);
          message.success('Save Successful.');
        },
        () => {
          message.error('Save Failed.');
        }
      );
  }

  handleActivityFormModalOk(id, item) {
    const { activities, dispatchUpdateTimelineActivities } = this.props;
    blockNavigation(true);

    const activity = activities.find(a => a.id === item.activity.key);

    dispatchUpdateTimelineActivities([{
      ...item,
      activity
    }]);
    message.success('Assignment updated');
  }

  handleActivityFormModalCancel() {
    this.setState({
      showActivityForm: false,
      selectedActivity: null
    });
  }

  handleBulkActivityModalCancel() {
    this.setState({
      showBulkActivityForm: false,
      selectedActivities: []
    });
  }

  handleItemContextClick(event) {
    const { key } = event;

    // Show the Edit Activity dialog for the selected item
    const { items, selected } = this.props;
    const activity = _.clone(items.find(item => item.id === event.item.props.data.key), true);

    const selectedActivities = items.filter(item => selected.includes(item.id));
    if (activity) {
      switch (key) {
      case 'edit':
        this.setState({
          showActivityForm: true,
          selectedActivity: activity
        });
        break;
      case 'editselected':
        if (selectedActivities.filter(item => item.is_new).length) {
          message.warn('Assignments must be saved before bulk editing');
        }
        else {
          this.setState({
            showBulkActivityForm: true,
            selectedActivities: selected
          });
        }
        break;
      default:
        break;
      }
    }
  }

  handleZoomIn() {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const { dispatchSelectTimelineActivities } = this.props;
    const diffMinutes = Math.max(visibleTimeEnd.diff(visibleTimeStart, 'minutes') / 2, MIN_ZOOM_DAYS * 24 * 60);
    const diffDays = Math.round(((diffMinutes / 60) / 24) * 100) / 100;

    dispatchSelectTimelineActivities();
    this.setState(prevState => ({
      visibleTimeEnd: moment(prevState.visibleTimeStart)
        .add(diffMinutes, 'minutes'),
      timelineSettings: {
        ...prevState.timelineSettings,
        options: {
          ...prevState.timelineSettings.options,
          timelineMaxDays: diffDays
        }
      }
    }));
  }

  handleZoomOut() {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const { dispatchSelectTimelineActivities } = this.props;
    const diffMinutes = Math.min(2 * visibleTimeEnd.diff(visibleTimeStart, 'minutes'), MAX_ZOOM_DAYS * 24 * 60);
    const diffDays = Math.round(((diffMinutes / 60) / 24) * 100) / 100;

    dispatchSelectTimelineActivities();
    this.setState(prevState => ({
      visibleTimeEnd: moment(prevState.visibleTimeStart)
        .add(diffMinutes, 'minutes'),
      timelineSettings: {
        ...prevState.timelineSettings,
        options: {
          ...prevState.timelineSettings.options,
          timelineMaxDays: diffDays
        }
      }
    }));
  }

  handlePanForward() {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const { dispatchSelectTimelineActivities } = this.props;
    const diffDays = visibleTimeEnd.diff(visibleTimeStart, 'days');
    const endDate = moment.min(moment(visibleTimeEnd)
      .add(diffDays, 'days'), this.MAX_VISIBLE_DATE);

    dispatchSelectTimelineActivities();
    this.setState({
      visibleTimeStart: moment(endDate)
        .add(-diffDays, 'days'),
      visibleTimeEnd: endDate
    });
  }

  handlePanBack() {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const { dispatchSelectTimelineActivities } = this.props;
    const diffDays = visibleTimeEnd.diff(visibleTimeStart, 'days');
    const startDate = moment.max(moment(visibleTimeStart)
      .add(-diffDays, 'days'), this.MIN_VISIBLE_DATE);

    dispatchSelectTimelineActivities();
    this.setState(() => ({
      visibleTimeStart: startDate,
      visibleTimeEnd: moment(startDate)
        .add(diffDays, 'days')
    }));
  }

  handleDeleteKeydown() {
    blockNavigation(true);
    const { dispatchDeleteTimelineActivity } = this.props;
    dispatchDeleteTimelineActivity();
  }

  handleCtrlKeydown() {
    const { timelineMode } = this.state;
    const resizeEnabled = isBitSet(Timeline.TIMELINE_MODES.RESIZE, timelineMode);

    if (!resizeEnabled) {
      let newTimelineMode = timelineMode;
      newTimelineMode &= ~TIMELINE_MODES.DRAG; // Turn off drag
      newTimelineMode |= TIMELINE_MODES.RESIZE; // Turn on resize
      this.setState({ timelineMode: newTimelineMode });
    }
  }

  handleCtrlKeyup() {
    const { timelineMode } = this.state;
    let newTimelineMode = timelineMode;
    newTimelineMode |= TIMELINE_MODES.DRAG; // Turn on drag
    newTimelineMode &= ~TIMELINE_MODES.RESIZE; // Turn off resize
    this.setState({ timelineMode: newTimelineMode });
  }

  handleMouseWheel(e) {
    const { dispatchSelectTimelineActivities } = this.props;
    if (!e.ctrlKey && !e.altKey && !e.shiftKey) {
      dispatchSelectTimelineActivities();
      const offsetTop = this.timelineContainer.scrollTop;
      this.timelineContainer.scrollTop = offsetTop + e.deltaY;
    }
  }

  handleSingleMouseClick(e, key) {
    // Update the user selection
    const { dispatchSelectTimelineActivities, selected } = this.props;
    const activityList = toggleListSelection(selected, key);

    dispatchSelectTimelineActivities(activityList);
  }

  handleItemClicks(e, key) {
    if (e.button !== MOUSE_RIGHT_CLICK) { // if user clicked anything other than the right
      this.handleSingleMouseClick(e, key);
    }
  }

  handleRowClick() {
    const { dispatchSelectTimelineActivities } = this.props;
    dispatchSelectTimelineActivities();
  }

  handleRowDoubleClick(e, rowNumber, clickedTime, snappedClickedTime) {
    const { dispatchAddTimelineActivity } = this.props;
    const { selectedToolbarItem, timelineSettings } = this.state;
    const { options } = timelineSettings;

    if (selectedToolbarItem) {
      blockNavigation(true);

      dispatchAddTimelineActivity(
        selectedToolbarItem,
        rowNumber,
        snappedClickedTime,
        options.activityCreateCount,
        options.activityHoursBetween
      );
    }
  }

  handleGroupDragDrop(e, group) {
    const { dispatchReorderTimelineGroup } = this.props;
    const fromGroup = JSON.parse(e.dataTransfer.getData('fromGroup'));
    dispatchReorderTimelineGroup(parseInt(fromGroup.id, 10), parseInt(group.id, 10));
  }

  handleGroupDelete(group) {
    const self = this;

    Modal.confirm({
      title: 'Delete User?',
      content: (
        <div>
          <p>This will remove all activities assigned to this user.</p>
        </div>
      ),
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk() {
        self.props.dispatchDeleteTimelineGroup(group.id);
      },
      onCancel() {
      }
    });
  }

  handleCustomFilters(customFilters) {
    this.setState(prevState => ({
      customFilters: {
        ...prevState.customFilters,
        ...customFilters
      }
    }));
  }

  handleTimelineInteraction(type, changes, items) {
    const { dispatchSelectTimelineActivities, dispatchMoveTimelineActivities, dispatchResizeTimelineActivities } = this.props;
    const { selected } = this.props;

    switch (type) {
    case Timeline.changeTypes.dragStart:
      return selected;

    case Timeline.changeTypes.dragEnd:
      blockNavigation(true);
      dispatchMoveTimelineActivities(changes, items);
      break;

    case Timeline.changeTypes.resizeStart:
      return selected;

    case Timeline.changeTypes.resizeEnd:
      blockNavigation(true);
      dispatchResizeTimelineActivities(changes, items);
      break;

    case Timeline.changeTypes.itemsSelected: {
      const activities = changes.map(item => item.key);
      dispatchSelectTimelineActivities(activities);
      break;
    }

    default:
      return changes;
    }
    return changes;
  }

  handleShowToolbar() {
    this.setState(prevState => ({
      showToolbar: !prevState.showToolbar
    }));
  }

  handleTimelineToolbarItemChange(item) {
    const itemType = (item ? item.type : '');
    if (itemType === 'activity') {
      this.setState({ selectedToolbarItem: item.data });
    }
  }

  handleTimelineToolbarOptionsChange(options) {
    this.setState(prevState => ({
      timelineSettings: {
        ...prevState.timelineSettings,
        options
      },
      visibleTimeEnd: moment(prevState.visibleTimeStart)
        .add(options.timelineMaxDays, 'day')
    }));
  }

  /**
   * Updates the list of pinned items in roster timeline store.
   * @param items New list of items to be saved in store.
   * @param itemType Identifier for `items`, e.g. 'activity', 'template'
   */
  handlePinItem(items, itemType) {
    const { dispatchUpdatePinnedActivities } = this.props;

    switch (itemType) {
      case 'activity': {
        dispatchUpdatePinnedActivities(items);
        break;
      }
      default:
        break;
    }
  }

  handleSaveTemplate() {
    this.saveTemplate();
  }

  unselectActivities(id) {
    const { selectedActivities } = this.state;
    this.setState({
      selectedActivities: selectedActivities.filter(i => i !== id)
    });
  }

  renderGroupTitle() {
    const { groups } = this.props;
    const title = `${groups.length - 1} User${groups.length === 1 ? '' : 's'}`;

    return (
      <div className="bhp-group-title">
        {title}
      </div>
    );
  }

  renderToolbar(showToolbar, isLoading) {
    const { permissions } = this.props;
    return (
      <ToolBar>
        <ToolBar.Right>
          <Tooltip title="Zoom out" placement="bottom">
            <Button size="small" icon="left" loading={isLoading} onClick={this.handlePanBack}>
              Pan backward
            </Button>
          </Tooltip>
          <Tooltip title="Zoom in" placement="bottom">
            <Button size="small" icon="right" loading={isLoading} onClick={this.handlePanForward}>
              Pan forwards
            </Button>
          </Tooltip>
          <Tooltip title="Zoom out" placement="bottom">
            <Button size="small" icon="minus" loading={isLoading} onClick={this.handleZoomOut}>
              Zoom out
            </Button>
          </Tooltip>
          <Tooltip title="Zoom in" placement="bottom">
            <Button size="small" icon="plus" loading={isLoading} onClick={this.handleZoomIn}>
              Zoom in
            </Button>
          </Tooltip>
          {permissions.CanAdminActivities()
            .hasPermissions() && (
            <Tooltip title="Save" placement="bottom">
              <Button className="saveButton" type="primary" size="small" icon="save" loading={isLoading} onClick={this.handleSaveTemplate}>
                Save
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Show Options" placement="bottom">
            <Button className="showToolbar" size="small" icon={showToolbar ? 'menu-unfold' : 'menu-fold'} onClick={this.handleShowToolbar}>
              {showToolbar ? 'Hide Options' : 'Show Options'}
            </Button>
          </Tooltip>
        </ToolBar.Right>
      </ToolBar>
    );
  }

  render() {
    const {
      selectedActivities, showBulkActivityForm, showActivityForm, showToolbar, timelineSettings, visibleTimeStart, visibleTimeEnd, timelineMode, selectedActivity
    } = this.state;
    const {
      dispatchLoadTemplateTimeline, match, groups, items, template, inFlight, selected, permissions, pinnedActivities
    } = this.props;
    const newUserId = Math.max(...groups.map(g => g.id));
    const templateId = match.params.id;

    // Show the following toolbar options
    const timelineToolbarTabs = {
      showActivities: permissions.CanAdminActivities()
        .hasPermissions(),
      showTemplates: false,
      showFilters: true,
      showFiltersToolbar: false,
      showOptions: permissions.CanAdminActivities()
        .hasPermissions(),
      showOptionsToolbar: false,
      optionFields: {
        showDisplayTravelLayer: false,
        showDisplayLeaveLayer: false,
        showDisplayAvailabilityLayer: false,
        showRosterPivot: false,
        showHideEmptyRows: false,
        showAssignmentTitleFormat: false,
        showActivityCreateCount: true,
        showActivityHoursBetween: true,
        showTimelineMaxDays: true,
        showTimelineSnapMinutes: true,
        showTimeFilter: false,
        showHideUnallocatedRows: false
      }
    };

    // Apply filter to the timeline activities
    const filteredItems = items.filter(this.filterActivities);
    const timelineItems = filteredItems.map(i => activitySelector(i));

    const selectedItems = items.filter(item => selectedActivities.includes(item.id))
      .map(i => activitySelector(i));

    return (
      <div className="bhp-template-timeline bhp-container">
        <KeyHandler keyEventName="keydown" keyCode={DELETE_KEY_CODE} onKeyHandle={this.handleDeleteKeydown} />
        <KeyHandler keyEventName="keydown" keyCode={BACKSPACE_KEY_CODE} onKeyHandle={this.handleDeleteKeydown} />
        <KeyHandler keyEventName="keyup" keyCode={CTRL_KEY_CODE} onKeyHandle={this.handleCtrlKeyup} />
        <KeyHandler keyEventName="keydown" keyCode={CTRL_KEY_CODE} onKeyHandle={this.handleCtrlKeydown} />
        <HeaderBar title={`Template: ${template.name}`} toolbar={this.renderToolbar(showToolbar, inFlight)} />
        <SlidePanel direction="right" width={365} show={showToolbar}>
          <Sidebar
            tabs={timelineToolbarTabs}
            defaultSettings={timelineSettings}
            options={timelineSettings.options}
            onItemChange={this.handleTimelineToolbarItemChange}
            onFilterChange={this.handleCustomFilters}
            onOptionsChange={this.handleTimelineToolbarOptionsChange}
            onPinItem={this.handlePinItem}
            pinnedActivities={pinnedActivities}
            config={{
              pickers,
              filters: {
                ...filters,
                showUserFilter: false
              }
            }}
            visibleTimeStart={null}
            visibleTimeEnd={null}
          />
          <Spin spinning={inFlight} indicator={<Icon type="loading" spin />} tip="Loading...">
            <div ref={this.getTimelineContainerRef} className="bhp-timeline" onWheel={this.handleMouseWheel}>
              <Timeline
                shallowUpdateCheck
                showCursorTime
                cursorTimeFormat="[Day] DDD - HH:mm"
                groups={groups}
                groupTitleRenderer={this.renderGroupTitle}
                groupRenderer={({ group }) => (
                  <GroupRenderer
                    canHover={false}
                    isUser
                    rowIndex={group.id === newUserId ? '+' : (group.id + 1).toString()}
                    title={group.title}
                    data={group}
                    isClickable={false}
                    isDraggable={group.id !== newUserId}
                    showDelete={group.id !== newUserId && permissions.CanAdminActivities()
                      .hasPermissions()}
                    onDragStart={TemplateTimeline.handleGroupDragStart}
                    onDragDrop={this.handleGroupDragDrop}
                    onDelete={this.handleGroupDelete}
                  />
                )}
                items={timelineItems}
                itemRenderer={({ ...props }) => (
                  <TemplateItemRenderer
                    {...props}
                    hiddenContextItems={[...(selected.length > 1 ? ['edit'] : []), 'reassign', 'action', ...(selected.length > 1 ? [] : ['editselected'])]}
                    onContextClick={this.handleItemContextClick}
                  />
                )}
                selectedItems={selected}
                timelineMode={permissions.CanAdminActivities()
                  .hasPermissions() ? timelineMode : 0}
                timebarFormat={TIMEBAR_FORMAT}
                startDate={visibleTimeStart}
                endDate={visibleTimeEnd}
                snapMinutes={Number(timelineSettings.options.timelineSnapMinutes)}
                onItemClick={this.handleItemClicks}
                onRowClick={this.handleRowClick}
                onRowDoubleClick={this.handleRowDoubleClick}
                onInteraction={this.handleTimelineInteraction}
              />
            </div>
          </Spin>
        </SlidePanel>
        {selectedActivity && (
          <Drawer
            placement="right"
            width={1200}
            closable
            maskClosable
            visible={showActivityForm}
            destroyOnClose
            onClose={this.handleActivityFormModalCancel}
            title="Edit Activity"
          >
            <Tabs
              defaultActiveKey="1"
            >
              <TabPane tab="Detail" key="1">
                <ActivityInput
                  canAdmin
                  isEditMode
                  ref={this.getActivityFormRef}
                  dataSource={selectedActivity}
                  title="Activity"
                  picker="time"
                  showNotes={false}
                  showTags={false}
                  onSaveClick={this.handleActivityFormModalOk}
                  onCancelClick={this.handleActivityFormModalCancel}
                  themeName="activity"
                />
              </TabPane>
              <TabPane tab="Notifications" key="2" disabled={selectedActivity && selectedActivity.is_new}>
                <Notifications
                  onDelete={() => dispatchLoadTemplateTimeline(templateId)}
                  onDeleteCallback={(response, callback) => {
                    callback();
                  }}
                  onSave={() => dispatchLoadTemplateTimeline(templateId)}
                  themeName="activitytemplate"
                  objectId={selectedActivity.id}
                  permissions={permissions}
                />
              </TabPane>
            </Tabs>
          </Drawer>
        )}
        <Drawer
          placement="right"
          width={1200}
          closable
          maskClosable
          visible={showBulkActivityForm}
          destroyOnClose
          onClose={this.handleBulkActivityModalCancel}
          title="Edit Bulk Activities"
        >
          <Tabs
            defaultActiveKey="1"
          >
            <TabPane tab="Notifications" key="1">
              <NotificationsPanel
                headerTitle="Selected Activities"
                itemRenderer={activityItemContent}
                items={items.map(item => ({
                  ...item,
                  notifications: item.default_notifications
                }))}
                onCancel={this.handleBulkActivityModalCancel}
                onDelete={() => dispatchLoadTemplateTimeline(templateId)}
                onDeselect={this.unselectActivities}
                onSave={() => dispatchLoadTemplateTimeline(templateId)}
                reducer={REDUCER_KEY.notificationRule}
                removeText="Remove all notifications from selected activities"
                selectedObjects={selectedActivities}
                selectedItems={selectedItems}
                themeName="activitytemplate"
                title="Notification"
              />
            </TabPane>
          </Tabs>
        </Drawer>
        <NavigationPrompt when={isNavigationBlocked()}>
          {(isOpen, onConfirm, onCancel) => (
            <Modal
              visible={isOpen}
              onOk={onCancel}
              onCancel={onConfirm}
              title="Unsaved Changes"
              okText="No - Remain here"
              cancelText="Yes - Discard Changes"
              maskClosable={false}
              closable={false}
            >
              <div>
                <p>
                  You have made changes to the timeline that have not been saved.
                </p>
                <p>
                  Do you wish to discard these changes?
                </p>
              </div>
            </Modal>
          )}
        </NavigationPrompt>
      </div>
    );
  }
}

TemplateTimeline.propTypes = {
  permissions: PropTypes.object.isRequired,
  template: PropTypes.object,
  activities: PropTypes.array,
  groups: PropTypes.array,
  items: PropTypes.array,
  inFlight: PropTypes.bool,
  match: PropTypes.object,
  router: PropTypes.object,
  route: PropTypes.object,
  selected: PropTypes.array.isRequired,
  pinnedActivities: PropTypes.array,

  dispatchNewTemplateTimeline: PropTypes.func,
  dispatchLoadTemplateTimeline: PropTypes.func,
  dispatchSaveTemplateTimeline: PropTypes.func,

  dispatchAddTimelineActivity: PropTypes.func,
  dispatchUpdateTimelineActivities: PropTypes.func,
  dispatchDeleteTimelineActivity: PropTypes.func,

  dispatchSelectTimelineActivities: PropTypes.func,
  dispatchMoveTimelineActivities: PropTypes.func,
  dispatchResizeTimelineActivities: PropTypes.func,

  dispatchReorderTimelineGroup: PropTypes.func,
  dispatchDeleteTimelineGroup: PropTypes.func,
  loadActivityList: PropTypes.func,
  dispatchUpdatePinnedActivities: PropTypes.func.isRequired,
};

TemplateTimeline.defaultProps = {
  template: {},
  groups: [],
  activities: [],
  items: [],
  inFlight: false,
  match: PropTypes.shape({
    params: PropTypes.object,
    router: PropTypes.object,
    route: PropTypes.string
  }).isRequired,
  pinnedActivities: [],
  router: {},
  route: {},

  dispatchNewTemplateTimeline() {
  },
  dispatchLoadTemplateTimeline() {
  },
  dispatchSaveTemplateTimeline() {
  },

  dispatchAddTimelineActivity() {
  },
  dispatchUpdateTimelineActivities() {
  },
  dispatchDeleteTimelineActivity() {
  },

  dispatchSelectTimelineActivities() {
  },
  dispatchMoveTimelineActivities() {
  },
  dispatchResizeTimelineActivities() {
  },

  dispatchReorderTimelineGroup() {
  },
  dispatchDeleteTimelineGroup() {
  },

  loadActivityList() {
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(TemplateTimeline);
